"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import { deploymentTiers } from "@/lib/data/homepage";

/**
 * Deployment Tiers — visual pyramid with persona count visualization,
 * use case icons, and 4 stacked cards with comparison data.
 */
export function DeploymentTiers() {
  const maxPersonas = 1000000;
  return (
    <section id="deployment-tiers" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-crimson)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-crimson)]" />
            Deployment Tiers
          </span>
          <h2 className="mt-6">
            Scale to Your Requirements
          </h2>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
          {/* LEFT — visual pyramid with persona counts */}
          <Reveal>
            <div className="rounded-3xl glass p-8 h-full relative overflow-hidden">
              <div className="eyebrow-tiny text-[var(--color-crimson)] mb-1">
                Persona Scale
              </div>
              <h3 className="mb-8">
                From 100 to 1,000,000+
              </h3>
              <div className="space-y-4">
                {deploymentTiers.slice().reverse().map((t) => {
                  const maxNum = parseInt(t.personas.replace(/[^0-9]/g, "").slice(0, 4)) || 1;
                  const w = Math.min(100, (maxNum / 1000) * 100);
                  return (
                    <div key={t.name} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold text-sm">{t.name}</div>
                        <div className="text-xs font-mono-num" style={{ color: t.accent }}>{t.personas}</div>
                      </div>
                      <div className="h-3 rounded-full bg-[rgb(var(--border)/0.15)] overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-1000"
                          style={{
                            width: `${w}%`,
                            background: `linear-gradient(90deg, ${t.accent}, color-mix(in oklch, ${t.accent} 50%, var(--color-purple)))`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-6 pt-6 border-t border-[rgb(var(--border)/0.15)] grid grid-cols-2 gap-4">
                <div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold">Max scale</div>
                  <div className="display-stat-sm mt-1" style={{ color: "var(--color-crimson)" }}>
                    1M+
                  </div>
                </div>
                <div>
                  <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] font-semibold">All tiers</div>
                  <div className="display-stat-sm mt-1" style={{ color: "var(--color-emerald)" }}>
                    95-98%
                  </div>
                  <div className="text-[0.6rem] text-[rgb(var(--fg-subtle))] mt-0.5">authenticity</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — 4 tier cards in 2x2 */}
          <div className="grid sm:grid-cols-2 gap-4">
            {deploymentTiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <div
                  className="rounded-2xl glass p-5 h-full relative overflow-hidden card-glow"
                  style={{ ["--glow" as string]: t.accent }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="grid place-items-center h-10 w-10 rounded-xl font-black text-base"
                      style={{
                        background: `linear-gradient(135deg, ${t.accent}, color-mix(in oklch, ${t.accent} 60%, var(--color-purple)))`,
                        color: "white",
                        boxShadow: `0 6px 20px -8px ${t.accent}`,
                      }}
                    >
                      0{i + 1}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg leading-tight" style={{ color: t.accent }}>{t.name}</h3>
                      <div className="eyebrow-tiny font-semibold text-[rgb(var(--fg-subtle))]">{t.scale}</div>
                    </div>
                  </div>
                  <div className="text-sm font-mono-num font-bold mb-2" style={{ color: t.accent }}>{t.personas}</div>
                  <p className="text-caption leading-relaxed text-[rgb(var(--fg-muted))]">{t.useCase}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}
